import type { SystemStyleObject } from "@chakra-ui/react"
import type { conditions } from "../chakra/themes/platform/theme"

type PanelStyleObject = SystemStyleObject & {
  [K in keyof typeof conditions as `_${K}`]?: SystemStyleObject
}

/**
 * Every style an augmentation panel needs that isn't already a theme token:
 * typography, fields, buttons, and menus. `chakra-react-select` can't be styled
 * this way and lives in `augmentationPanelSelectorStyles`.
 *
 * These are style-only bags, checked as style objects rather than declared as
 * one component's props — they get spread onto inputs, textareas, select
 * triggers, buttons, and menu items alike. Typing them as `InputProps` or
 * `ButtonProps` dragged along `children` and `type`, which those controls each
 * declare differently.
 *
 * The panel's vocabulary lives in the platform theme, not here: the
 * `augmentationPanel` colors, radii, sizes, spacing, shadows, font, and text
 * style. Reach for a token before adding a literal.
 */

// -- Typography ---------------------------------------------------------------

// The panel's type face as a raw value, for the `!important` rule below.
// Everything else should carry `textStyle: "augmentationPanel"`.
const AUGMENTATION_PANEL_FONT_FAMILY = "var(--chakra-fonts-augmentation-panel)"

/** Non-clickable labels / static copy (Figma: 60% opacity). */
export const AUGMENTATION_PANEL_STATIC_TEXT_CSS: SystemStyleObject = {
  textStyle: "augmentationPanel",
  opacity: 0.6
}

export const AUGMENTATION_PANEL_TYPOGRAPHY_CSS: SystemStyleObject = {
  // Panels host legacy content that brings its own type, so the family is
  // forced on every descendant rather than left to each component.
  "&, & :where(*)": {
    fontFamily: `${AUGMENTATION_PANEL_FONT_FAMILY} !important`
  },
  "& :where([class^='pwai-'], [class*=' pwai-'])": {
    fontFamily: "'pathicons' !important"
  },
  // Field labels are non-clickable static text (Figma: 60% opacity). Scoped to
  // fields: a checkbox's label is the row's content, not a label beside it.
  "& :where(label.chakra-field__label, [data-scope='field'][data-part='label'])":
    AUGMENTATION_PANEL_STATIC_TEXT_CSS
}

// -- Fields -------------------------------------------------------------------

/**
 * iOS Safari zooms the page in when a field it focuses sets its text below
 * 16px, and a zoomed page carries the panel's sides off screen. Keyed to the
 * absence of hover — a touch device — so a pointer keeps the panel's 14px.
 *
 * The line box grows with the text: the panel's 17px would crop a descender at
 * this size. Carried by the field bags below, and by any editable surface that
 * states its own type rather than taking one of them.
 */
export const AUGMENTATION_PANEL_FIELD_TOUCH_TEXT_CSS = {
  _mediaHoverNone: {
    fontSize: "16px",
    lineHeight: "20px"
  }
} as const

const AUGMENTATION_PANEL_FIELD_BASE_PROPS = {
  bg: "augmentationPanel.fill.muted",
  borderRadius: "augmentationPanelField",
  textStyle: "augmentationPanel",
  ...AUGMENTATION_PANEL_FIELD_TOUCH_TEXT_CSS,
  _focusVisible: {
    boxShadow: "augmentationPanelFocusRing"
  }
} as const

export const AUGMENTATION_PANEL_INPUT_PROPS = {
  ...AUGMENTATION_PANEL_FIELD_BASE_PROPS,
  h: "augmentationPanelRow",
  px: "augmentationPanelGutter",
  _mediaHoverNone: {
    ...AUGMENTATION_PANEL_FIELD_TOUCH_TEXT_CSS._mediaHoverNone,
    h: "augmentationPanelRowTouch",
    minH: "augmentationPanelRowTouch"
  }
} as const satisfies PanelStyleObject

export const AUGMENTATION_PANEL_TEXTAREA_PROPS = {
  ...AUGMENTATION_PANEL_FIELD_BASE_PROPS,
  p: "augmentationPanelGutter"
} as const satisfies SystemStyleObject

/** Dropdown / select controls inside augmentation panels (Figma: 15px radius). */
export const AUGMENTATION_PANEL_SELECT_PROPS = {
  ...AUGMENTATION_PANEL_INPUT_PROPS,
  // Keep the native select recipe's end padding for its indicator.
  px: undefined,
  ps: AUGMENTATION_PANEL_INPUT_PROPS.px,
  borderRadius: "augmentationPanel"
} as const satisfies SystemStyleObject

/** Non-clickable labels / static copy, as props rather than nested CSS. */
export const AUGMENTATION_PANEL_STATIC_TEXT_PROPS = {
  textStyle: "augmentationPanel",
  opacity: 0.6,
  m: 0
} as const satisfies SystemStyleObject

// -- Scrolling ----------------------------------------------------------------

// How far the fade reaches in from an edge with more content past it.
const SCROLL_FADE = "16px"

const SCROLL_FADE_TOP = `transparent, #000 ${SCROLL_FADE}`
const SCROLL_FADE_BOTTOM = `#000 calc(100% - ${SCROLL_FADE}), transparent`

/**
 * The panel's scrolling region, for a `data-overflow-top` / `data-overflow-bottom`
 * host (see `useScrollOverflow`).
 *
 * A touch platform draws its scrollbar only while a finger is down, so a panel
 * whose content runs past the screen looks like a panel that simply ends
 * there. Fading the content at an edge it continues past says otherwise
 * without spending any of the panel's width — and it says it on a pointer
 * device too, alongside the bar.
 */
export const AUGMENTATION_PANEL_SCROLL_CSS: SystemStyleObject = {
  overflowY: "auto",
  overscrollBehavior: "contain",
  scrollbarColor: "rgba(0, 0, 0, 0.25) transparent",
  scrollbarWidth: "thin",
  "&[data-overflow-top][data-overflow-bottom]": {
    maskImage: `linear-gradient(to bottom, ${SCROLL_FADE_TOP}, ${SCROLL_FADE_BOTTOM})`
  },
  "&[data-overflow-top]:not([data-overflow-bottom])": {
    maskImage: `linear-gradient(to bottom, ${SCROLL_FADE_TOP})`
  },
  "&[data-overflow-bottom]:not([data-overflow-top])": {
    maskImage: `linear-gradient(to bottom, ${SCROLL_FADE_BOTTOM})`
  }
}

// -- Selection controls -------------------------------------------------------

/**
 * The panel's checkmark: a hairline ring that fills with the panel's ink when
 * checked, the way a Reminders list does. Round rather than square because the
 * panel's own geometry is round, and a 4px-radius square in a 15px pill reads
 * as a control borrowed from somewhere else.
 *
 * Sized to the panel's icon slot so a list of checkboxes lands its labels on
 * the same left edge as a list of icons. The row is the hit area, so the ring
 * doesn't have to carry a touch target.
 *
 * Shaped to be worn by any Chakra checkmark control — checkbox today, radio or
 * switch when something needs one.
 */
export const AUGMENTATION_PANEL_CHECK_CONTROL_PROPS = {
  bg: "transparent",
  borderColor: "augmentationPanel.fill.bold",
  borderRadius: "full",
  borderWidth: "1.5px",
  boxSize: "augmentationPanelIcon",
  color: "white",
  // Insets the checkmark so its stroke doesn't crowd the ring.
  p: "2.5px",
  transition: "background-color 120ms ease, border-color 120ms ease",
  _checked: {
    bg: "augmentationPanel.text",
    borderColor: "augmentationPanel.text"
  },
  _focusVisible: {
    boxShadow: "augmentationPanelFocusRing",
    outline: "none"
  }
} as const satisfies SystemStyleObject

/**
 * A switch track on the same ink logic: the panel's fill ramp when off, solid
 * panel ink when on. Chakra's default track is a flat gray that doesn't belong
 * to the ramp everything else in the panel is stated in.
 */
export const AUGMENTATION_PANEL_SWITCH_CONTROL_PROPS = {
  bg: "augmentationPanel.fill.muted",
  _checked: { bg: "augmentationPanel.text" },
  _focusVisible: {
    boxShadow: "augmentationPanelFocusRing",
    outline: "none"
  }
} as const satisfies SystemStyleObject

// -- Buttons ------------------------------------------------------------------

/** A panel-height button, one weight up from the panel's text style. */
export const AUGMENTATION_PANEL_ACTION_BUTTON_PROPS = {
  borderRadius: "button",
  h: "augmentationPanelRow",
  minH: "augmentationPanelRow",
  textStyle: "augmentationPanel",
  fontWeight: 600,
  _mediaHoverNone: {
    h: "augmentationPanelRowTouch",
    minH: "augmentationPanelRowTouch"
  }
} as const

/** Submit / apply. Muted rather than branded: the panel is a working surface. */
export const AUGMENTATION_PANEL_SUBMIT_BUTTON_PROPS = {
  bg: "augmentationPanel.fill.muted",
  color: "augmentationPanel.text",
  _hover: { bg: "augmentationPanel.fill.strong" }
} as const satisfies SystemStyleObject

/** Discard. The one place the panel departs from its black-alpha ramp. */
export const AUGMENTATION_PANEL_DISCARD_BUTTON_PROPS = {
  bg: "rgba(240, 0, 0, 0.1)",
  color: "#f00000",
  _hover: { bg: "rgba(240, 0, 0, 0.15)" }
} as const satisfies SystemStyleObject

// -- Menus --------------------------------------------------------------------

// Menus borrow the panel's vocabulary so they read as part of the panel rather
// than generic Chakra chrome. Two flavors: destination menus that switch
// context use the panel's list pills, while contextual options menus stay flat
// so they don't read as a list of things.
const MENU_ROW_GAP = "5px"

/** Icon-to-label gap within an options menu row. */
export const AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_GAP = "10px"

/** Floating menu surface: panel chrome (translucent, blurred, panel shadow). */
export const AUGMENTATION_PANEL_MENU_CONTENT_PROPS = {
  backdropFilter: "blur(10px)",
  bg: "augmentationPanel.menuSurface",
  border: "none",
  borderRadius: "augmentationPanel",
  boxShadow: "augmentationPanel",
  minW: 0,
  overflow: "hidden",
  p: MENU_ROW_GAP,
  w: "fit-content"
} as const

const MENU_ITEM_ACTIVE_CSS: SystemStyleObject = {
  bg: "augmentationPanel.fill.muted",
  color: "augmentationPanel.text"
}

const MENU_ITEM_TEXT_CSS: SystemStyleObject = {
  alignItems: "center",
  justifyContent: "flex-start",
  px: "augmentationPanelGutter",
  py: 0,
  textStyle: "augmentationPanel",
  w: "100%"
}

// Written as selectors (not _hover/_highlighted) so these objects can be nested
// under a parent selector as well as spread onto a component.
const MENU_ITEM_ACTIVE_SELECTOR =
  "&:hover, &[data-highlighted], &[data-state='open']"

/**
 * Rows that navigate somewhere — a change of context, like the panel's root
 * selector. Styled as panel list pills since each row is a destination.
 */
export const AUGMENTATION_PANEL_MENU_ITEM_CSS: SystemStyleObject = {
  ...MENU_ITEM_TEXT_CSS,
  bg: "augmentationPanel.fill.subtle",
  borderRadius: "augmentationPanel",
  gap: "augmentationPanel",
  minH: "augmentationPanelRow",
  "@media (hover: none)": {
    minH: "augmentationPanelRowTouch"
  },
  [MENU_ITEM_ACTIVE_SELECTOR]: MENU_ITEM_ACTIVE_CSS
}

/**
 * Rows that act on the thing you opened the menu from (edit, move, delete).
 * Flat and compact — a resting fill per row would read as a list of items
 * rather than a set of actions.
 */
export const AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_CSS: SystemStyleObject = {
  ...MENU_ITEM_TEXT_CSS,
  bg: "transparent",
  borderRadius: "augmentationPanelField",
  gap: AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_GAP,
  minH: "augmentationPanelOptionsRow",
  [MENU_ITEM_ACTIVE_SELECTOR]: MENU_ITEM_ACTIVE_CSS
}

/** Icons in menu rows match the panel list item icon treatment. */
export const AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS: SystemStyleObject = {
  boxSize: "augmentationPanelIcon",
  opacity: 0.8
}

/**
 * A panel heading that opens a menu. Treated as a text menu rather than a
 * control in a container: no fill or padding, so the header keeps its
 * typographic hierarchy and stays flush with the panel content edge. The
 * chevron carries the affordance, sharpening on hover and while open.
 */
export const AUGMENTATION_PANEL_MENU_TRIGGER_PROPS = {
  bg: "transparent",
  color: "augmentationPanel.text",
  h: "19px",
  minH: "19px",
  minW: 0,
  p: 0,
  _hover: { bg: "transparent", "& .chakra-icon": { opacity: 1 } },
  _open: { bg: "transparent", "& .chakra-icon": { opacity: 1 } }
} as const

/**
 * Restyles an `AugmentationToolbar` rendered inside a panel dropdown so its
 * options match panel list items. The toolbar itself is shared with the
 * floating island, so this stays scoped to the dropdown that applies it.
 */
export const AUGMENTATION_PANEL_MENU_TOOLBAR_CSS: SystemStyleObject = {
  "& .AugmentationToolbarGroup": {
    backdropFilter: "none",
    bg: "transparent",
    gap: MENU_ROW_GAP,
    p: 0
  },
  "& .ToolbarStack": { gap: MENU_ROW_GAP },
  "& .AugmentationToolbarButton": AUGMENTATION_PANEL_MENU_ITEM_CSS,
  "& .AugmentationToolbarButton .chakra-stack": { gap: "augmentationPanel" },
  // Matches by the Chakra icon class rather than `.Pathicon`: the toolbar
  // renders the web package's Pathicon, which doesn't set that class.
  "& .AugmentationToolbarButton .chakra-icon":
    AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS
}

export const AUGMENTATION_PANEL_AVATAR_CSS: SystemStyleObject = {
  boxSize: "augmentationPanelIcon",
  maxH: "augmentationPanelIcon",
  maxW: "augmentationPanelIcon",
  minW: "augmentationPanelIcon",
  "& .chakra-avatar__fallback": {
    fontSize: "7px"
  }
}
